<script lang="ts">
	import { twMerge } from 'tailwind-merge';
	import { CrossIcon } from '../../../../assets/icons/CrossIcon';
	import { FilterIcon } from '../../../../assets/icons/FilterIcon';
	import { Button } from '../../../../components/Button';
	import type { DialogStores } from '../../../../components/Dialog';
	import { Drawer } from '../../../../components/Drawer';
	import { ScrollHintOverlays } from '../../../../components/ScrollHintOverlays';
	import { Txt } from '../../../../components/Txt';
	import {
		initialVerticalScrollState,
		verticalScrollHint,
		type VerticalScrollState
	} from '../../../../utils/verticalScrollHint/verticalScrollHint';
	import type { FilterSections } from '../../types';
	import { applyFilters } from '../../utils/applyFilters/applyFilters';
	import { resetFilter } from '../../utils/resetFilter/resetFilter';
	import { FilterTypes } from '../FilterTypes';
	import type { FilterTypesClasses } from '../FilterTypes/FilterTypes.svelte';

	interface Props {
		classes: FilterTypesClasses;
		filterSections: FilterSections;
		withMaxSectionHeight: boolean;
		drawerStores: DialogStores;
		scrollTarget?: string;
	}

	let {
		classes,
		filterSections,
		withMaxSectionHeight,
		drawerStores,
		scrollTarget = undefined
	}: Props = $props();

	let screenWidth: number | undefined = $state();

	let open = $derived(drawerStores.states.open);

	// Drives the top/bottom scroll shadows so it's clear more filter sections
	// exist beyond the fixed header/footer without having to scroll to discover them.
	let bodyScrollState: VerticalScrollState = $state(initialVerticalScrollState);

	const handleApplyClick = () => {
		const navigation = applyFilters({ scrollTarget });
		open.update(() => false);
		return navigation;
	};

	const handleResetClick = () => {
		resetFilter();
	};
</script>

<svelte:window bind:innerWidth={screenWidth} />

<Drawer dataCy="filter" {drawerStores} class="max-w-[100%] sm:max-w-[450px]">
	<div class="relative h-full overflow-hidden">
		<div
			class="border-b-1 bg-gray-0 flex h-[60px] items-center justify-between border-b border-b-gray-200 px-4 py-4 md:px-6"
		>
			<span class="flex items-center gap-2">
				<FilterIcon class="h-[20px] w-[20px]" />
				<Txt variant="h5">Filters</Txt>
			</span>
			<button
				type="button"
				aria-label="Close filters"
				data-cy={`filter-drawer-close-button`}
				onclick={() => open.set(false)}><CrossIcon class="h-[20px] w-[20px]" /></button
			>
		</div>

		<div class="xs:h-[calc((100%-60px)-70px)] relative h-[calc((100%-60px)-124px)]">
			<div
				use:verticalScrollHint={{
					onChange: (state) => {
						bodyScrollState = state;
					}
				}}
				class={twMerge('h-full overflow-y-auto bg-gray-50', classes.body)}
			>
				<FilterTypes {classes} {filterSections} {withMaxSectionHeight} />
			</div>

			<ScrollHintOverlays dataCy="filter-drawer-body" scrollState={bodyScrollState} />
		</div>

		<div
			class="border-t-1 xs:h-[70px] absolute bottom-[1px] left-0 right-0 h-[124px] border-t border-t-gray-200 pt-4"
		>
			<div class="xs:grid-cols-2 grid grid-cols-1 gap-4 px-4 pb-4 md:px-6">
				<Button
					variant="secondary"
					size="md"
					dataCy={`filter-drawer-reset-button`}
					onclick={handleResetClick}>Reset</Button
				>

				<Button
					size="md"
					class="xs:order-last order-first"
					dataCy={`filter-drawer-apply-button`}
					onclick={handleApplyClick}>Apply Filters</Button
				>
			</div>
		</div>
	</div>
</Drawer>
